import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import moment from "moment";
import {Spin} from "antd";
import {ClockCircleOutlined, SearchOutlined} from "@ant-design/icons";
import {observer} from "mobx-react-lite";
import {useHistory, useLocation} from "react-router-dom";
import {useStore} from "hooks/useStore";
import {useFetch} from "hooks/useFetch";
import {LiveOperationFilters} from "./components/LiveOperationFilters";
import {LiveOperationMap} from "./components/LiveOperationMap";
import {ActiveOrdersTable} from "./components/ActiveOrdersTable";
import {ActiveShiftsTable, SHIFT_STATUS_ORDER} from "./components/ActiveShiftsTable";
import {useLiveOperationPolling} from "./hooks/useLiveOperationPolling";
import {filterCourierStatesByCompanyAndPool} from "./utils/courierFilterUtils";
import {ORDER_STATUS_FILTERS} from "./utils/orderGrouping";
import {
    buildCourierStatsFromShiftRows,
    buildCourierStatsFromCourierStates
} from "./utils/shiftStats";
import {
    PageRoot,
    FiltersBar,
    TablesColumn,
    TabsRow,
    TabsBar,
    TabButton,
    TabCount,
    TabFilters,
    FilterSelect,
    FilterSearchInput,
    ActiveTableRow,
    MapColumn,
    LiveOperationGlobalStyle
} from "./LiveOperationPage.styles";

const BURNING_ORDERS_DURATION_SECONDS = 420;
const FETCH_LIMIT = 200;

// Filtre değişiminde eski veriyi göstermemek için kullanılan sabit boş değerler. Modül düzeyinde
// tutuluyor ki her render'da yeni referans üretip bağlı useMemo'ları boşuna tetiklemesinler.
const EMPTY_COURIER_MAP_DATA = {courierStateList: [], filterKey: null};
const EMPTY_ORDER_DATA = {rows: [], dataCount: 0, filterKey: null};
const EMPTY_SHIFTS_DATA = {rows: [], filterKey: null};

// Şehir yetkisi olmayan kullanıcıda (admin vb.) açılış şehri — meStore'daki başlangıç değeriyle
// aynı tutuluyor.
const DEFAULT_CITY = "istanbul";

export const LiveOperationPage = observer(() => {
    const stores = useStore();
    const history = useHistory();
    const location = useLocation();
    const filters = stores.meStore.liveOperation.filters;
    const options = stores.meStore.liveOperation.options;

    // Şehir filtresi localStorage'da kalıcı. "Tümü" seçili bırakılmışsa sayfa her açılışta
    // (ve her yenilemede) ülke/çok şehir geneli sorgu atardı — pahalı. Açılışta yetkili ilk
    // şehre çekilir; kullanıcı gerçekten tümünü istiyorsa filtreden yeniden seçer.
    // Effect'te değil RENDER'da yapılıyor: effect fetch'lerden sonra çalışır ve "Tümü" isteği
    // bir kez yine de gider, ardından ikinci bir istek daha atılırdı.
    const cityNormalizedRef = useRef(false);
    if (!cityNormalizedRef.current) {
        cityNormalizedRef.current = true;
        if (filters.city === "all") {
            stores.meStore.liveOperation.filters.city =
                stores.meStore.auth?.cities?.[0] || DEFAULT_CITY;
        }
    }

    // Harita switch'leri (Kurye/Şube/Sipariş) localStorage'da kalıcı, ama bu sayfa her açılışta
    // hep aynı kombinasyonla başlamalı: kullanıcının bir önceki oturumda bıraktığı seçim önemsiz.
    // Şehir normalizasyonuyla aynı desen: RENDER'da, bir kez çalışır.
    const optionsNormalizedRef = useRef(false);
    if (!optionsNormalizedRef.current) {
        optionsNormalizedRef.current = true;
        stores.meStore.liveOperation.options.showCouriers = true;
        stores.meStore.liveOperation.options.showSubCompanies = false;
        stores.meStore.liveOperation.options.showOrders = false;
    }

    const searchParams = new URLSearchParams(location.search);
    const activeTab = searchParams.get("tab") === "shifts" ? "shifts" : "orders";
    const setActiveTab = tab => {
        const params = new URLSearchParams(location.search);
        params.set("tab", tab);
        history.replace({pathname: location.pathname, search: params.toString()});
    };

    const [ordersCount, setOrdersCount] = useState(0);
    const [shiftsCount, setShiftsCount] = useState(0);
    const [shiftsSearchText, setShiftsSearchText] = useState("");

    const [ordersStatusFilter, setOrdersStatusFilter] = useState("all");
    const [ordersCourierSearchText, setOrdersCourierSearchText] = useState("");
    const [shiftsStatusFilter, setShiftsStatusFilter] = useState("all");
    const [shiftsStartTimeFilter, setShiftsStartTimeFilter] = useState("all");

    const {mapRecall, ordersRecall, shiftsRecall} = useLiveOperationPolling();

    const dedicatedCompanyId = filters.company === "pool" ? "" : filters.company;
    const today = moment().toDate();

    // useFetch yeni yanıt gelene kadar eldeki veriyi tutuyor; filtre değiştiğinde ekran bir an
    // için ESKİ (daha geniş) kapsamı gösteriyordu — haritada filtre hiç uygulanmamış gibi
    // görünüyor, dahası fitBounds o eski pinlere göre zoom yapıyordu. Her yanıt, alındığı filtre
    // anahtarıyla saklanır ve tüketilmeden önce anahtar karşılaştırılır: böylece "elimdeki veri
    // güncel filtreye mi ait" sorusu effect sırasına bağlı kalmadan RENDER anında yanıtlanır.
    // Polling tazelemesi anahtarı değiştirmediği için 30 sn'lik yenilemede boşalma olmaz.
    const dataFilterKey = JSON.stringify([
        filters.city,
        dedicatedCompanyId,
        filters.company,
        filters.subCompany,
        filters.pool,
        filters.zoneManagementId,
        filters.zoneId
    ]);
    const forFilterKey = (data, fallback) => (data?.filterKey === dataFilterKey ? data : fallback);

    // Kurye canlı durumu — hem harita hem Aktif Vardiyalar tablosu (Durum sütunu) bunu kullanır,
    // tek çağrıyla paylaşılır.
    const [, courierMapRaw] = useFetch({
        fetchFn: async () => {
            const data = await stores.courierStore.getCourierMapList({
                city: filters.city,
                types: [],
                statuses: [],
                dedicatedCompanyId,
                dedicatedSubCompanies: filters.subCompany || [],
                dedicatedPoolIds: filters.pool || [],
                includePoolsOfCompany: true,
                zoneManagementId: filters.zoneManagementId,
                zoneId: filters.zoneId
            });
            return {courierStateList: data?.courierStateList || [], filterKey: dataFilterKey};
        },
        waitForResponse: true,
        dependencies: [
            filters.city,
            dedicatedCompanyId,
            JSON.stringify(filters.subCompany),
            JSON.stringify(filters.pool),
            filters.zoneManagementId,
            filters.zoneId,
            mapRecall
        ],
        condition: Boolean(filters.city),
        defaultDataValue: {courierStateList: [], filterKey: null}
    });
    const courierMapData = forFilterKey(courierMapRaw, EMPTY_COURIER_MAP_DATA);

    // Tablo üstü filtreler haritayı da besler, ama YALNIZCA açık sekmeninkiler. Pasif sekmenin
    // filtresi state'te duruyor (sekme değişince sıfırlanmıyor); sekmeye göre kapatılmazsa
    // kullanıcının artık görmediği bir filtre haritayı sessizce daraltmaya devam eder.
    const activeCourierSearchText =
        activeTab === "orders" ? ordersCourierSearchText : shiftsSearchText;
    const activeOrdersStatusFilter = activeTab === "orders" ? ordersStatusFilter : "all";
    const activeShiftsStatusFilter = activeTab === "shifts" ? shiftsStatusFilter : "all";
    const activeShiftsStartTimeFilter = activeTab === "shifts" ? shiftsStartTimeFilter : "all";

    const courierList = useMemo(() => {
        let list = filterCourierStatesByCompanyAndPool(courierMapData?.courierStateList, {
            companyId: filters.company
        });
        const needle = activeCourierSearchText.trim().toLocaleLowerCase("tr-TR");
        if (needle) {
            list = list.filter(courierState =>
                (courierState?.courier?.fullname || "").toLocaleLowerCase("tr-TR").includes(needle)
            );
        }
        return list;
    }, [courierMapData?.courierStateList, filters.company, activeCourierSearchText]);

    const orderListParams = {
        skip: 0,
        limit: FETCH_LIMIT,
        city: filters.city,
        companies: filters.company && filters.company !== "pool" ? [filters.company] : [],
        subcompanies: filters.subCompany || [],
        poolIds: filters.pool || [],
        burningOrdersDuration: BURNING_ORDERS_DURATION_SECONDS,
        sortField: {field: "", asc: true},
        searchText: "",
        courierName: "",
        deliveryStatusOverrides: [],
        workType: "all",
        integrationType: "",
        zoneManagementId: filters.zoneManagementId,
        zoneId: filters.zoneId
    };

    const [queueLoading, queueRaw, , , refetchQueue] = useFetch({
        fetchFn: async () => {
            const data = await stores.orderStore.getOrderList({
                ...orderListParams,
                deliveryStatus: "inQueue"
            });
            return {
                rows: data?.rows || [],
                dataCount: data?.dataCount || 0,
                filterKey: dataFilterKey
            };
        },
        waitForResponse: true,
        dependencies: [
            filters.city,
            filters.company,
            JSON.stringify(filters.subCompany),
            JSON.stringify(filters.pool),
            filters.zoneManagementId,
            filters.zoneId,
            ordersRecall
        ],
        defaultDataValue: {rows: [], dataCount: 0, filterKey: null}
    });

    const queueData = forFilterKey(queueRaw, EMPTY_ORDER_DATA);

    const [currentLoading, currentRaw, , , refetchCurrent] = useFetch({
        fetchFn: async () => {
            const data = await stores.orderStore.getOrderList({
                ...orderListParams,
                deliveryStatus: "current"
            });
            return {
                rows: data?.rows || [],
                dataCount: data?.dataCount || 0,
                filterKey: dataFilterKey
            };
        },
        waitForResponse: true,
        dependencies: [
            filters.city,
            filters.company,
            JSON.stringify(filters.subCompany),
            JSON.stringify(filters.pool),
            filters.zoneManagementId,
            filters.zoneId,
            ordersRecall
        ],
        defaultDataValue: {rows: [], dataCount: 0, filterKey: null}
    });

    const currentData = forFilterKey(currentRaw, EMPTY_ORDER_DATA);

    const [shiftsLoading, shiftsRaw, , , refetchShifts] = useFetch({
        fetchFn: async () => {
            const data = await stores.shiftStore.getShiftForOperationPage({
                forecastDate: moment(today).format("YYYY-MM-DD"),
                city: filters.city,
                company: dedicatedCompanyId,
                subcompanies: filters.subCompany || [],
                poolIds: filters.pool || [],
                searchText: shiftsSearchText,
                zoneManagementId: filters.zoneManagementId,
                zoneId: filters.zoneId
            });
            return {rows: data?.rows || [], filterKey: dataFilterKey};
        },
        waitForResponse: true,
        dependencies: [
            filters.city,
            dedicatedCompanyId,
            JSON.stringify(filters.subCompany),
            JSON.stringify(filters.pool),
            filters.zoneManagementId,
            filters.zoneId,
            shiftsSearchText,
            shiftsRecall
        ],
        defaultDataValue: {rows: [], filterKey: null}
    });
    const shiftsData = forFilterKey(shiftsRaw, EMPTY_SHIFTS_DATA);

    // Başlangıç Saati filtresinin seçenekleri sabit bir liste değil: ekrandaki vardiyaların
    // fiili başlangıç saatlerinden ("startLabel", ör. "18:00") çıkarılır. Böylece listede
    // olmayan bir saat filtre olarak sunulmaz.
    const shiftsStartTimeOptions = useMemo(() => {
        const uniqueLabels = [
            ...new Set((shiftsData?.rows || []).map(row => row.startLabel).filter(Boolean))
        ].sort();
        return uniqueLabels.map(label => ({value: label, label}));
    }, [shiftsData?.rows]);

    // Durum filtresinin seçenekleri de sabit bir liste değil: ekrandaki vardiyaların fiili
    // durumlarından çıkarılır (Başlangıç Saati filtresiyle aynı desen). Sıralama SHIFT_STATUS_ORDER
    // ile korunur ki liste alfabetik değil akışın mantıksal sırasında görünsün.
    const shiftsStatusOptions = useMemo(() => {
        const presentStatuses = new Set(
            (shiftsData?.rows || []).map(row => row.status).filter(Boolean)
        );
        return SHIFT_STATUS_ORDER.filter(status => presentStatuses.has(status)).map(status => ({
            value: status,
            label: status
        }));
    }, [shiftsData?.rows]);

    // Haritanın yükleniyor göstergesi yalnızca KULLANICI kaynaklı isteklerde çıkar: filtre
    // değişimi ve ilk açılış. Polling tazelemesi filtre anahtarını değiştirmediği için elde
    // güncel anahtarlı veri durur ve harita 20/30 sn'de bir örtülmez.
    const mapDataLoading =
        courierMapRaw?.filterKey !== dataFilterKey ||
        queueRaw?.filterKey !== dataFilterKey ||
        currentRaw?.filterKey !== dataFilterKey;

    // Vardiyalar ekranındaki Durum ve Başlangıç Saati filtreleri de haritaya (kurye + sipariş
    // pinleri) etki eder — Kurye Ara zaten sunucu tarafında shiftsData'yı daralttığı için ayrıca
    // burada uygulanmasına gerek yok.
    const shiftFilteredCourierIds = useMemo(() => {
        const hasStatusFilter = activeShiftsStatusFilter && activeShiftsStatusFilter !== "all";
        const hasStartTimeFilter =
            activeShiftsStartTimeFilter && activeShiftsStartTimeFilter !== "all";
        if (!hasStatusFilter && !hasStartTimeFilter) return null;
        return new Set(
            (shiftsData?.rows || [])
                .filter(row => !hasStatusFilter || row.status === activeShiftsStatusFilter)
                .filter(
                    row => !hasStartTimeFilter || row.startLabel === activeShiftsStartTimeFilter
                )
                .map(row => row.courierId && String(row.courierId))
                .filter(Boolean)
        );
    }, [shiftsData?.rows, activeShiftsStatusFilter, activeShiftsStartTimeFilter]);

    const mapCourierList = useMemo(() => {
        if (!shiftFilteredCourierIds) return courierList;
        return courierList.filter(courierState =>
            shiftFilteredCourierIds.has(String(courierState._id))
        );
    }, [courierList, shiftFilteredCourierIds]);

    // Sayaç kutusu aktif sekmenin tablosuyla eşleşsin diye kaynak sekmeye göre değişir:
    // Vardiyalar sekmesinde shifts/operation-page satırlarından, Siparişler sekmesinde
    // courier/maplist'ten (mevcut davranış). Vardiya tarafında da tablo üstü Durum/Başlangıç
    // Saati filtreleri ve Firma="Havuz" seçimi uygulanır ki kutu haritadaki pin kümesiyle aynı
    // kapsamı göstersin.
    const courierStats = useMemo(() => {
        if (activeTab !== "shifts") return buildCourierStatsFromCourierStates(mapCourierList);

        let rows = shiftsData?.rows || [];
        if (filters.company === "pool") {
            rows = rows.filter(row => row.shiftType === "pool");
        }
        if (activeShiftsStatusFilter && activeShiftsStatusFilter !== "all") {
            rows = rows.filter(row => row.status === activeShiftsStatusFilter);
        }
        if (activeShiftsStartTimeFilter && activeShiftsStartTimeFilter !== "all") {
            rows = rows.filter(row => row.startLabel === activeShiftsStartTimeFilter);
        }
        return buildCourierStatsFromShiftRows(rows);
    }, [
        activeTab,
        mapCourierList,
        shiftsData?.rows,
        filters.company,
        activeShiftsStatusFilter,
        activeShiftsStartTimeFilter
    ]);

    // Harita yalnızca kullanıcı filtre değiştirdiğinde fitBounds yapar; polling ile gelen veri
    // tazelemeleri kullanıcının pan/zoom'unu bozmamalı. İmza türetilmiş listelerden değil ham
    // filtre değerlerinden kurulur — türetilmiş veriler her fetch'te yeni referans üretiyor.
    const filterSignature = useMemo(
        () =>
            JSON.stringify([
                filters.city,
                filters.company,
                filters.subCompany,
                filters.pool,
                filters.zoneManagementId,
                filters.zoneId,
                activeTab,
                ordersStatusFilter,
                ordersCourierSearchText,
                shiftsStatusFilter,
                shiftsStartTimeFilter,
                shiftsSearchText
            ]),
        [
            filters.city,
            filters.company,
            JSON.stringify(filters.subCompany),
            JSON.stringify(filters.pool),
            filters.zoneManagementId,
            filters.zoneId,
            activeTab,
            ordersStatusFilter,
            ordersCourierSearchText,
            shiftsStatusFilter,
            shiftsStartTimeFilter,
            shiftsSearchText
        ]
    );

    const reloadOrders = () => {
        refetchQueue();
        refetchCurrent();
    };

    // Tabloda seçilen sipariş ya da kuryenin haritada tek başına gösterilmesi için ortak state;
    // tablo seçer, harita tüketir. `type: "order"` → {key, deliveryId, orderId, orderNumber},
    // `type: "courier"` → {key, courierId, courierName}.
    const [focusedTarget, setFocusedTarget] = useState(null);
    const clearFocus = useCallback(() => setFocusedTarget(null), []);

    // Filtre değişimi odaktaki hedefi listeden düşürmüş olabilir; odak korunursa harita
    // alakasız bir kapsamda kilitli kalır.
    useEffect(() => setFocusedTarget(null), [filterSignature]);

    return (
        <PageRoot>
            <LiveOperationGlobalStyle />
            <FiltersBar>
                <LiveOperationFilters />
            </FiltersBar>

            <TablesColumn>
                <TabsRow>
                    <TabsBar>
                        <TabButton
                            $active={activeTab === "orders"}
                            onClick={() => setActiveTab("orders")}
                        >
                            Aktif Siparişler
                            <TabCount>
                                {queueLoading || currentLoading ? (
                                    <Spin size="small" />
                                ) : (
                                    `(${ordersCount})`
                                )}
                            </TabCount>
                        </TabButton>
                        <TabButton
                            $active={activeTab === "shifts"}
                            onClick={() => setActiveTab("shifts")}
                        >
                            Vardiyalar
                            <TabCount>
                                {shiftsLoading ? <Spin size="small" /> : `(${shiftsCount})`}
                            </TabCount>
                        </TabButton>
                    </TabsBar>

                    <TabFilters>
                        {activeTab === "orders" ? (
                            <React.Fragment key="orders-filters">
                                <FilterSelect
                                    value={ordersStatusFilter}
                                    onChange={setOrdersStatusFilter}
                                    options={ORDER_STATUS_FILTERS}
                                />
                                <FilterSearchInput
                                    allowClear
                                    prefix={<SearchOutlined />}
                                    placeholder="Kurye ara..."
                                    defaultValue={ordersCourierSearchText}
                                    onPressEnter={e => setOrdersCourierSearchText(e.target.value)}
                                    onChange={e => {
                                        if (!e.target.value) setOrdersCourierSearchText("");
                                    }}
                                />
                            </React.Fragment>
                        ) : (
                            <React.Fragment key="shifts-filters">
                                <FilterSelect
                                    dropdownMatchSelectWidth={false}
                                    allowClear
                                    placeholder="Vardiya Durum"
                                    value={
                                        shiftsStatusFilter === "all" ? undefined : shiftsStatusFilter
                                    }
                                    onChange={value => setShiftsStatusFilter(value || "all")}
                                    options={shiftsStatusOptions}
                                />
                                <FilterSelect
                                    dropdownMatchSelectWidth={false}
                                    allowClear
                                    suffixIcon={<ClockCircleOutlined />}
                                    placeholder="Başlangıç Saati"
                                    value={
                                        shiftsStartTimeFilter === "all"
                                            ? undefined
                                            : shiftsStartTimeFilter
                                    }
                                    onChange={value => setShiftsStartTimeFilter(value || "all")}
                                    options={shiftsStartTimeOptions}
                                />
                                <FilterSearchInput
                                    allowClear
                                    prefix={<SearchOutlined />}
                                    placeholder="Kurye ara..."
                                    defaultValue={shiftsSearchText}
                                    onPressEnter={e => setShiftsSearchText(e.target.value)}
                                    onChange={e => {
                                        if (!e.target.value) setShiftsSearchText("");
                                    }}
                                />
                            </React.Fragment>
                        )}
                    </TabFilters>
                </TabsRow>

                <ActiveTableRow>
                    <div
                        style={{
                            display: activeTab === "orders" ? "flex" : "none",
                            flexDirection: "column",
                            flex: 1,
                            minHeight: 0,
                            overflow: "hidden"
                        }}
                    >
                        <ActiveOrdersTable
                            queueData={queueData}
                            currentData={currentData}
                            onReload={reloadOrders}
                            onCountChange={setOrdersCount}
                            statusFilter={ordersStatusFilter}
                            courierSearchText={ordersCourierSearchText}
                            focusedKey={focusedTarget?.key}
                            onFocusChange={setFocusedTarget}
                        />
                    </div>
                    <div
                        style={{
                            display: activeTab === "shifts" ? "flex" : "none",
                            flexDirection: "column",
                            flex: 1,
                            minHeight: 0,
                            overflow: "hidden"
                        }}
                    >
                        <ActiveShiftsTable
                            rows={shiftsData?.rows}
                            onReload={refetchShifts}
                            onCountChange={setShiftsCount}
                            statusFilter={shiftsStatusFilter}
                            startTimeFilter={shiftsStartTimeFilter}
                        />
                    </div>
                </ActiveTableRow>
            </TablesColumn>

            <MapColumn>
                <LiveOperationMap
                    filters={filters}
                    options={options}
                    onOptionsChange={next => {
                        stores.meStore.liveOperation.options = next;
                    }}
                    mapRecall={mapRecall}
                    courierList={mapCourierList}
                    courierStats={courierStats}
                    queueDeliveries={queueData?.rows}
                    currentDeliveries={currentData?.rows}
                    courierSearchText={activeCourierSearchText}
                    orderStatusFilter={activeOrdersStatusFilter}
                    shiftCourierIds={shiftFilteredCourierIds}
                    filterSignature={filterSignature}
                    loading={mapDataLoading}
                    focusedTarget={focusedTarget}
                    onClearFocus={clearFocus}
                />
            </MapColumn>
        </PageRoot>
    );
});
